import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import {
  CreateSocialUploadDtoSourceType,
  EnrichedVideoEditorEditListItemDto,
  GetFragmentDto,
  ListEditsVideoEditorEditsQueryResponse,
} from '@app/modules/fragments/fragment.types';
import { useFragmentQuery } from '@app/modules/fragments/hooks/useFragment';
import {
  SOCIAL_EXPORT_FRAGMENT_SOURCE,
  useAvailableSocialExportSources,
} from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { useFragmentMediaUrlQuery } from '@app/modules/fragments/hooks/useFragmentMediaUrlQuery';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import {
  type SocialExportPublishForm,
  useSocialExportUploadConnectionFlow,
} from '@app/modules/social-export/hooks/useSocialExportUploadConnectionFlow';
import { publishMessages } from '@app/modules/social-export/publish';
import { getEditRenderedVideoUrl } from '@app/modules/video-editions';
import {
  SocialExportGetStationConnectionStateDto,
  useWebAppCreateFragmentSocialUploads,
  webAppGetFragmentSocialUploadsByFragmentIdQueryKey,
  webAppListEditsVideoEditorEditsQueryKey,
} from '@mi-front/api-v2';
import { type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

export type { SocialExportPublishForm };

export interface UseSocialExportUploadPageResult {
  fragmentId: string;
  platform: SocialPlatform;
  fragmentQuery: ReturnType<typeof useFragmentQuery>;
  mediaUrlQuery: ReturnType<typeof useFragmentMediaUrlQuery>;
  settingsQuery: ReturnType<typeof useSocialExportUploadConnectionFlow>['settingsQuery'];
  editsListQuery: UseQueryResult<ListEditsVideoEditorEditsQueryResponse, unknown> & {
    queryKey: ReturnType<typeof webAppListEditsVideoEditorEditsQueryKey>;
  };
  selectedSource: string;
  setSelectedSource: (value: string) => void;
  edits: EnrichedVideoEditorEditListItemDto[];
  isFragmentSourceAllowed: boolean;
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  hasSelectedRenderedEdit: boolean;
  renderedVideoUrl: string | null;
  PublishForm: SocialExportPublishForm;
  handleUpload: (fragment: GetFragmentDto, data: Record<string, unknown>) => Promise<void>;
  connections: SocialExportGetStationConnectionStateDto[];
  selectedConnectionId: string | null;
  setSelectedConnectionId: (id: string | null) => void;
  areConnectionsResolved: boolean;
}

/**
 * Owns routing, source selection, connection settings, rendered preview URL, and the
 * create-upload mutation for the fragment social export upload flow.
 */
export function useSocialExportUploadPage(): UseSocialExportUploadPageResult {
  const { fragmentId, platform: platformParam } = useParams<{
    fragmentId: string;
    platform: string;
  }>();
  const platform = socialPlatforms.get(platformParam!.toUpperCase())!;
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  /** Preselected video-editor edit id from the upload URL (`editId` preferred; `projectId` kept for backward compatibility). */
  const searchParams = new URLSearchParams(search);
  const editIdFromUrl = searchParams.get('editId') ?? searchParams.get('projectId') ?? undefined;

  const [selectedSource, setSelectedSource] = useState<string>(
    () => editIdFromUrl ?? SOCIAL_EXPORT_FRAGMENT_SOURCE,
  );

  const fragmentQuery = useFragmentQuery(fragmentId!);
  const mediaUrlQuery = useFragmentMediaUrlQuery(fragmentId!);
  const { editsListQuery, edits, editsWithRender, isFragmentSourceAllowed, hasAvailableSources } =
    useAvailableSocialExportSources({
      fragmentId: fragmentId!,
      stationId: fragmentQuery.data?.stationId,
    });

  const {
    connections,
    selectedConnectionId,
    setSelectedConnectionId,
    areConnectionsResolved,
    isConnectionsLoading,
    settingsQuery,
    PublishForm,
  } = useSocialExportUploadConnectionFlow(fragmentQuery.data?.stationId, platform);

  const selectedEdit =
    selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE
      ? (edits.find((edit) => edit.id === selectedSource) ?? null)
      : null;

  const { url: renderedVideoUrl } = getEditRenderedVideoUrl(selectedEdit, {
    mode: 'playback',
  });

  const showMediaSelector = editsWithRender.length > 0;
  const hasSelectedRenderedEdit = editsWithRender.some((edit) => edit.id === selectedSource);

  useEffect(() => {
    if (editIdFromUrl) {
      setSelectedSource(editIdFromUrl);
    }
  }, [editIdFromUrl]);

  useEffect(() => {
    if (editIdFromUrl || isFragmentSourceAllowed) return;
    if (selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE) return;

    const firstRenderedEditId = editsWithRender[0]?.id;
    if (!firstRenderedEditId) return;

    setSelectedSource(firstRenderedEditId);
  }, [isFragmentSourceAllowed, editIdFromUrl, editsWithRender, selectedSource]);

  const uploadMutation = useWebAppCreateFragmentSocialUploads({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: webAppGetFragmentSocialUploadsByFragmentIdQueryKey(fragmentId!),
        });
        notifyInfo($t(publishMessages.publicationStarted));
      },
      onError: (error: Error) => {
        notifyError($t(publishMessages.startPublicationError));
        console.log(error);
      },
    },
  });

  const handleUpload = async (fragment: GetFragmentDto, data: Record<string, unknown>) => {
    try {
      if (selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE && !isFragmentSourceAllowed) {
        notifyError($t(publishMessages.noAllowedSourceError));
        return;
      }

      if (isConnectionsLoading) {
        notifyError($t(publishMessages.connectionNotReady));
        return;
      }

      if (!selectedConnectionId) {
        notifyError(
          $t(publishMessages.noOAuthConnectionForPlatform, {
            platformName: platform.name,
          }),
        );
        return;
      }

      const sourceType =
        selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE
          ? CreateSocialUploadDtoSourceType.FRAGMENT
          : CreateSocialUploadDtoSourceType.VIDEO_EDITOR_EDIT;
      const sourceId =
        selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE ? fragment.id : selectedSource;

      await uploadMutation.mutateAsync({
        data: {
          sourceType,
          sourceId,
          connectionId: selectedConnectionId,
          data,
        },
      });
      pushTo(`/fragments/${fragment.id}/publish`);
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  return {
    fragmentId: fragmentId!,
    platform,
    fragmentQuery,
    mediaUrlQuery,
    settingsQuery,
    editsListQuery,
    selectedSource,
    setSelectedSource,
    edits,
    isFragmentSourceAllowed,
    hasAvailableSources,
    showMediaSelector,
    hasSelectedRenderedEdit,
    renderedVideoUrl,
    PublishForm,
    handleUpload,
    connections,
    selectedConnectionId,
    setSelectedConnectionId,
    areConnectionsResolved,
  };
}
